/**
 * /try UI bootstrap config resolver.
 *
 * WHAT THIS FILE DOES:
 * - Reads `window.__GIST_TRY_UI_CONFIG__` injected by `src/ssr/try.ts`.
 * - Returns a normalized `{ createModuleTabsEnabled }` shape.
 * - Treats a missing or malformed config as "tabs disabled" so the legacy
 *   single-textarea layout stays the default in production.
 *
 * WHY THIS EXISTS:
 * - Keeps the create-module tabs rollout flag isolated from
 *   `window.__GIST_TRY_CONFIG__` (Firebase auth) and
 *   `window.__GIST_TRY_MEDIA_CONFIG__` (transcode controls). Each global owns
 *   one concern.
 * - Gives `src/client/try.ts` a single resolver instead of inlining boolean
 *   normalization at the bootstrap site.
 *
 * RELATED:
 * - src/ssr/try.ts injects this global only when the env flag is on.
 * - src/client/try-mode-tabs.ts mounts the tabbed surface when enabled.
 */

/**
 * Bootstrap shape resolved from `window.__GIST_TRY_UI_CONFIG__`.
 */
export interface TryUiBootstrapConfig {
  /**
   * When true, the /try client mounts the five-tab create module.
   * Default false; flag-off behavior is byte-identical to today's /try.
   */
  createModuleTabsEnabled: boolean;
}

const TryUiBootstrapDefaults = Object.freeze({
  createModuleTabsEnabled: false
}) satisfies TryUiBootstrapConfig;

/**
 * Resolves a normalized bootstrap config from unknown input.
 *
 * Tolerates `null`, `undefined`, primitives, or objects with the wrong shape;
 * any of those collapse to defaults. Booleans pass through.
 */
export function resolveTryUiBootstrapConfig(raw: unknown): TryUiBootstrapConfig {
  if (raw === null || typeof raw !== 'object') {
    return { ...TryUiBootstrapDefaults };
  }
  const value = (raw as { createModuleTabsEnabled?: unknown }).createModuleTabsEnabled;
  return {
    createModuleTabsEnabled:
      typeof value === 'boolean'
        ? value
        : TryUiBootstrapDefaults.createModuleTabsEnabled
  };
}

declare global {
  interface Window {
    /**
     * Optional /try UI bootstrap config injected by `src/ssr/try.ts`.
     *
     * Only present when the create-module-tabs env flag is on. Absence MUST be
     * treated as the safe default (tabs disabled) — never throw on missing.
     */
    __GIST_TRY_UI_CONFIG__?: TryUiBootstrapConfig | null;
  }
}
